import { usePopper } from "react-popper"
import { PropsWithChildren, useCallback, useEffect, useState } from "react"
import { useClickAway, useToggle, useUnmount } from "react-use"
import { createPortal } from "react-dom"
import { Placement } from "@popperjs/core"
import cn from "classnames"

import { getOrCreatePortalNode } from "modules/Call/common/utils/getOrCreatePortalNode"
import { Popover } from "modules/Call/components/SafetyPopover/Popover"
import { Shield } from "modules/Call/components/SafetyPopover/Shield"

function allowToHide(popoverNode: Node, eventTarget: EventTarget) {
    return !(eventTarget instanceof HTMLAnchorElement && popoverNode.contains(eventTarget))
}

interface Props {
    maxWidth: number | string
    placement?: Placement
    className?: string
    popoverBodyClassName?: string
    iconClassName?: string
    onToggle?: (shown: boolean) => void
}

export function SafetyPopover({
    maxWidth,
    children,
    placement = "top",
    className,
    popoverBodyClassName,
    iconClassName,
    onToggle,
}: PropsWithChildren<Props>) {
    const [shown, toggle] = useToggle(false)
    const [popover, setPopoverElement] = useState<HTMLDivElement | null>(null)
    const [handler, setHandlerElement] = useState<HTMLDivElement | null>(null)
    const portalNode = getOrCreatePortalNode()
    useUnmount(() => portalNode.remove())
    const { styles, attributes } = usePopper(handler, popover, {
        placement,
    })
    const hide = useCallback(() => toggle(false), [])
    const show = useCallback(() => toggle(true), [])
    useClickAway({ current: handler }, (event) => {
        if (popover && event.target) {
            allowToHide(popover, event.target) && hide()
        }
    })
    useEffect(() => {
        onToggle?.(shown)
    }, [shown])

    return (
        <>
            <Shield
                className={cn("cursor-pointer", className)}
                iconClassName={iconClassName}
                ref={setHandlerElement}
                onClick={() => {
                    show()
                }}
            >
                {children}
            </Shield>
            {createPortal(
                <Popover
                    ref={setPopoverElement}
                    style={{
                        ...styles.popper,
                        maxWidth: maxWidth,
                    }}
                    {...attributes.popper}
                    className={popoverBodyClassName}
                    show={show}
                    shown={shown}
                    hide={hide}
                />,
                portalNode
            )}
        </>
    )
}
